/* ------------------------------------------------------------------
   EmailAutomations.js — Marketing → Email Marketing → Automations
   Linear drip builder: pick a trigger, then an ordered list of steps
   (send email / wait / add tag / remove tag / add to list). Activate to
   run. Two views: 'list' and 'builder'.
------------------------------------------------------------------ */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import EmailBlockEditor, { renderBlocksToHtml } from './EmailBlockEditor';

const ax = axios.create({ withCredentials: true });

const TRIGGERS = [
  { value: 'list_join',      label: 'Contact joins a list' },
  { value: 'bio_subscribe',  label: 'Signs up via Link-in-Bio' },
  { value: 'tag_added',      label: 'Tag is added' },
  { value: 'status_changed', label: 'Status changes' },
];
const STATUS_BADGE = {
  active: { label: 'Active', bg: '#dcfce7', color: '#166534' },
  paused: { label: 'Paused', bg: '#fef9c3', color: '#854d0e' },
  draft:  { label: 'Draft',  bg: '#f3f4f6', color: '#6b7280' },
};
const STEP_LABEL = { send_email: 'Send email', wait: 'Wait', add_tag: 'Add tag', remove_tag: 'Remove tag', add_to_list: 'Add to list' };
const blankStep = (type) => ({ type, config: type === 'wait' ? { days: 3 } : type === 'send_email' ? { subject: '', bodyHtml: '' } : type === 'add_to_list' ? { listId: '' } : { tag: '' } });
const blankAuto = () => ({ id: null, name: '', status: 'draft', triggerType: 'list_join', triggerConfig: {}, steps: [blankStep('send_email')] });

export default function EmailAutomations() {
  const [view, setView] = useState('list');
  const [items, setItems] = useState([]);
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [draft, setDraft] = useState(blankAuto());
  const [saving, setSaving] = useState(false);
  const [userId, setUserId] = useState(null);
  const [designing, setDesigning] = useState(null);   // step index being designed

  const flash = (text, ok = true) => { setMsg({ text, ok }); setTimeout(() => setMsg(null), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, l] = await Promise.all([ax.get('/api/email/automations'), ax.get('/api/email/lists')]);
      setItems(a.data.automations || []);
      setLists(l.data.lists || []);
      try { const me = await ax.get('/api/auth/me'); if (me.data?.user?.id) setUserId(me.data.user.id); } catch { /* ignore */ }
    } catch (e) { console.error('load automations', e); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  function newAuto() { setDraft(blankAuto()); setView('builder'); }
  async function editAuto(a) {
    try {
      const { data } = await ax.get(`/api/email/automations/${a.id}`);
      const p = data.automation;
      setDraft({ id: p.id, name: p.name || '', status: p.status, triggerType: p.trigger_type, triggerConfig: p.trigger_config || {}, steps: (p.steps && p.steps.length) ? p.steps : [blankStep('send_email')] });
      setView('builder');
    } catch { flash('Could not open automation', false); }
  }

  async function save(activate) {
    if (!draft.name.trim()) { flash('Name your automation.', false); return; }
    if (draft.steps.some(s => s.type === 'send_email' && !(s.config.subject || '').trim())) { flash('Every email step needs a subject.', false); return; }
    setSaving(true);
    const body = { name: draft.name.trim(), triggerType: draft.triggerType, triggerConfig: draft.triggerConfig, steps: draft.steps };
    try {
      let id = draft.id;
      if (id) await ax.put(`/api/email/automations/${id}`, body);
      else { const r = await ax.post('/api/email/automations', body); id = r.data.id; setDraft(d => ({ ...d, id })); }
      if (activate) { await ax.post(`/api/email/automations/${id}/status`, { status: 'active' }); flash('Automation activated.'); setView('list'); load(); }
      else flash('Saved.');
    } catch (e) { flash(e.response?.data?.error || 'Save failed', false); }
    finally { setSaving(false); }
  }

  async function toggleStatus(a) {
    const next = a.status === 'active' ? 'paused' : 'active';
    try { await ax.post(`/api/email/automations/${a.id}/status`, { status: next }); load(); }
    catch { flash('Could not change status', false); }
  }
  async function remove(a) {
    if (!window.confirm(`Delete "${a.name}"? Enrolled contacts stop receiving it.`)) return;
    try { await ax.delete(`/api/email/automations/${a.id}`); load(); } catch { flash('Delete failed', false); }
  }

  /* step helpers */
  const setStep = (i, patch) => setDraft(d => ({ ...d, steps: d.steps.map((s, j) => j === i ? { ...s, config: { ...s.config, ...patch } } : s) }));
  const addStep = (type) => setDraft(d => ({ ...d, steps: [...d.steps, blankStep(type)] }));
  const removeStep = (i) => setDraft(d => ({ ...d, steps: d.steps.filter((_, j) => j !== i) }));
  const moveStep = (i, dir) => setDraft(d => { const a = [...d.steps]; const j = i + dir; if (j < 0 || j >= a.length) return d; [a[i], a[j]] = [a[j], a[i]]; return { ...d, steps: a }; });
  const setTrigCfg = (patch) => setDraft(d => ({ ...d, triggerConfig: { ...d.triggerConfig, ...patch } }));

  /* ───────── BUILDER ───────── */
  if (view === 'builder') {
    return (
      <div style={{ maxWidth: 720 }}>
        {designing !== null && draft.steps[designing] && (
          <DesignEmailModal
            initial={draft.steps[designing].config || {}}
            userId={userId}
            onSave={(patch) => { setStep(designing, patch); setDesigning(null); }}
            onClose={() => setDesigning(null)}
          />
        )}
        <button onClick={() => { setView('list'); load(); }} style={{ ...ghostBtn, marginBottom: 14 }}>← Back to automations</button>
        {msg && <Banner msg={msg} />}

        <Card title="Automation">
          <Label>Name</Label>
          <input value={draft.name} onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} placeholder="e.g. Welcome series" style={inputStyle} />
        </Card>

        <Card title="Trigger — when this starts" style={{ marginTop: 14 }}>
          <select value={draft.triggerType} onChange={e => setDraft(d => ({ ...d, triggerType: e.target.value, triggerConfig: {} }))} style={{ ...inputStyle, background: '#fff' }}>
            {TRIGGERS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <div style={{ marginTop: 10 }}>
            {(draft.triggerType === 'list_join' || draft.triggerType === 'bio_subscribe') && (
              <>
                <Label>{draft.triggerType === 'bio_subscribe' ? 'List (optional)' : 'Which list'}</Label>
                <select value={draft.triggerConfig.listId || ''} onChange={e => setTrigCfg({ listId: e.target.value ? Number(e.target.value) : '' })} style={{ ...inputStyle, background: '#fff' }}>
                  <option value="">{draft.triggerType === 'bio_subscribe' ? 'Any bio signup' : 'Select a list…'}</option>
                  {lists.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </>
            )}
            {draft.triggerType === 'tag_added' && (
              <><Label>Tag</Label><input value={draft.triggerConfig.tag || ''} onChange={e => setTrigCfg({ tag: e.target.value })} placeholder="e.g. vip" style={inputStyle} /></>
            )}
            {draft.triggerType === 'status_changed' && (
              <><Label>New status</Label>
                <select value={draft.triggerConfig.status || ''} onChange={e => setTrigCfg({ status: e.target.value })} style={{ ...inputStyle, background: '#fff' }}>
                  <option value="">Any status change</option><option value="lead">Lead</option><option value="subscriber">Subscriber</option><option value="customer">Customer</option>
                </select>
              </>
            )}
          </div>
        </Card>

        <Card title="Steps — what happens" style={{ marginTop: 14 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {draft.steps.map((s, i) => (
              <div key={i} style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: 13, color: '#111827' }}>{i + 1}. {STEP_LABEL[s.type]}</span>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button onClick={() => moveStep(i, -1)} style={iconBtn} title="Move up">↑</button>
                    <button onClick={() => moveStep(i, 1)} style={iconBtn} title="Move down">↓</button>
                    <button onClick={() => removeStep(i)} style={{ ...iconBtn, color: '#dc2626' }} title="Remove">✕</button>
                  </div>
                </div>
                {s.type === 'send_email' && (
                  <>
                    <input value={s.config.subject || ''} onChange={e => setStep(i, { subject: e.target.value })} placeholder="Subject" style={inputStyle} />
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                      <button type="button" onClick={() => setDesigning(i)} style={primaryBtn}>🎨 Design email</button>
                      <span style={{ fontSize: 13, color: (s.config.bodyHtml || (s.config.bodyBlocks && s.config.bodyBlocks.length)) ? '#059669' : '#9ca3af' }}>
                        {(s.config.bodyHtml || (s.config.bodyBlocks && s.config.bodyBlocks.length)) ? '✓ Designed' : 'No content yet'}
                      </span>
                    </div>
                  </>
                )}
                {s.type === 'wait' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 14, color: '#374151' }}>Wait</span>
                    <input type="number" min={0} value={s.config.days ?? 0} onChange={e => setStep(i, { days: Math.max(0, parseInt(e.target.value, 10) || 0) })} style={{ ...inputStyle, width: 80 }} />
                    <span style={{ fontSize: 14, color: '#374151' }}>day(s)</span>
                  </div>
                )}
                {(s.type === 'add_tag' || s.type === 'remove_tag') && (
                  <input value={s.config.tag || ''} onChange={e => setStep(i, { tag: e.target.value })} placeholder="Tag name" style={inputStyle} />
                )}
                {s.type === 'add_to_list' && (
                  <select value={s.config.listId || ''} onChange={e => setStep(i, { listId: e.target.value ? Number(e.target.value) : '' })} style={{ ...inputStyle, background: '#fff' }}>
                    <option value="">Select a list…</option>
                    {lists.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                  </select>
                )}
              </div>
            ))}
            {!draft.steps.length && <div style={{ color: '#9ca3af', fontSize: 14 }}>No steps yet — add one below.</div>}
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
            {Object.keys(STEP_LABEL).map(t => <button key={t} onClick={() => addStep(t)} style={miniBtn}>+ {STEP_LABEL[t]}</button>)}
          </div>
        </Card>

        <div style={{ display: 'flex', gap: 10, marginTop: 16 }}>
          <button onClick={() => save(false)} disabled={saving} style={{ ...ghostBtn, opacity: saving ? 0.6 : 1 }}>{saving ? 'Saving…' : 'Save draft'}</button>
          <button onClick={() => save(true)} disabled={saving} style={{ ...primaryBtn, opacity: saving ? 0.6 : 1 }}>Save &amp; activate</button>
        </div>
      </div>
    );
  }

  /* ───────── LIST ───────── */
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 12, marginBottom: 18 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: '0 0 4px' }}>Automations</h1>
          <p style={{ color: '#6b7280', fontSize: 14, margin: 0 }}>Emails that send themselves when a contact does something.</p>
        </div>
        <button onClick={newAuto} style={primaryBtn}>+ New automation</button>
      </div>
      {msg && <Banner msg={msg} />}
      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden' }}>
        {loading ? <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
          : items.length === 0 ? <div style={{ padding: 48, textAlign: 'center', color: '#9ca3af' }}>No automations yet. Create a welcome series, a nurture sequence, and more.</div>
          : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead><tr style={{ background: '#f8fafc', color: '#6b7280', textAlign: 'left' }}><Th>Name</Th><Th>Trigger</Th><Th>Steps</Th><Th>Active</Th><Th>Status</Th><Th></Th></tr></thead>
            <tbody>
              {items.map(a => {
                const sb = STATUS_BADGE[a.status] || STATUS_BADGE.draft;
                const trig = (TRIGGERS.find(t => t.value === a.trigger_type) || {}).label || a.trigger_type;
                return (
                  <tr key={a.id} style={{ borderTop: '1px solid #f1f5f9' }}>
                    <Td><button onClick={() => editAuto(a)} style={linkBtn}>{a.name}</button></Td>
                    <Td><span style={{ color: '#6b7280' }}>{trig}</span></Td>
                    <Td>{a.step_count}</Td>
                    <Td>{a.active_count}</Td>
                    <Td><span style={{ fontSize: 12, fontWeight: 600, padding: '2px 9px', borderRadius: 99, background: sb.bg, color: sb.color }}>{sb.label}</span></Td>
                    <Td>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button onClick={() => editAuto(a)} style={miniBtn}>Edit</button>
                        <button onClick={() => toggleStatus(a)} style={miniBtn}>{a.status === 'active' ? 'Pause' : 'Activate'}</button>
                        <button onClick={() => remove(a)} style={{ ...miniBtn, color: '#dc2626', borderColor: '#fecaca' }}>Delete</button>
                      </div>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

/* ── design-email modal (same designer as campaigns) ── */
function DesignEmailModal({ initial, userId, onSave, onClose }) {
  const [mode, setMode] = useState((initial.bodyBlocks && initial.bodyBlocks.length) ? 'design' : (initial.bodyHtml ? 'html' : 'design'));
  const [blocks, setBlocks] = useState(Array.isArray(initial.bodyBlocks) ? initial.bodyBlocks : []);
  const [html, setHtml] = useState(initial.bodyHtml || '');
  const composed = () => mode === 'design' ? renderBlocksToHtml(blocks) : html;
  const tBtn = (active) => ({ padding: '5px 12px', background: active ? '#eff6ff' : '#fff', color: active ? '#2563eb' : '#6b7280', border: `1px solid ${active ? '#bfdbfe' : '#d1d5db'}`, borderRadius: 7, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' });
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(17,24,39,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1100, padding: 16, overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 1150, marginTop: 12, boxShadow: '0 24px 70px rgba(0,0,0,0.3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #f1f5f9' }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#111827' }}>Design email</div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button type="button" onClick={() => setMode('design')} style={tBtn(mode === 'design')}>Design</button>
            <button type="button" onClick={() => setMode('html')} style={tBtn(mode === 'html')}>HTML</button>
            <button type="button" onClick={onClose} style={{ ...miniBtn, border: 'none', fontSize: 15 }}>✕</button>
          </div>
        </div>
        <div style={{ padding: 18 }}>
          {mode === 'design'
            ? <EmailBlockEditor blocks={blocks} onChange={setBlocks} userId={userId} />
            : <textarea value={html} onChange={e => setHtml(e.target.value)} rows={14} placeholder="<h1>Hi!</h1><p>Your email…</p>" style={{ ...inputStyle, resize: 'vertical', fontFamily: 'monospace', fontSize: 13 }} />}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 20px', borderTop: '1px solid #f1f5f9' }}>
          <button type="button" onClick={onClose} style={ghostBtn}>Cancel</button>
          <button type="button" onClick={() => onSave({ bodyHtml: composed(), bodyBlocks: mode === 'design' ? blocks : null })} style={primaryBtn}>Save email</button>
        </div>
      </div>
    </div>
  );
}

/* ── styles ── */
const inputStyle = { width: '100%', padding: '9px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, color: '#111827', outline: 'none', boxSizing: 'border-box' };
const primaryBtn = { padding: '9px 16px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' };
const ghostBtn   = { padding: '9px 14px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' };
const miniBtn    = { padding: '5px 10px', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer' };
const iconBtn    = { padding: '3px 8px', background: '#fff', color: '#374151', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer' };
const linkBtn    = { background: 'none', border: 'none', padding: 0, color: '#2563eb', fontWeight: 600, fontSize: 14, cursor: 'pointer' };
const Label = ({ children, style }) => <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6, ...style }}>{children}</div>;
const Th = ({ children }) => <th style={{ padding: '10px 14px', fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{children}</th>;
const Td = ({ children }) => <td style={{ padding: '11px 14px', color: '#111827' }}>{children}</td>;
const Card = ({ title, children, style }) => (
  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', ...style }}>
    <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 16 }}>{title}</div>
    {children}
  </div>
);
const Banner = ({ msg }) => (
  <div style={{ marginBottom: 14, padding: '10px 14px', borderRadius: 8, fontSize: 14, fontWeight: 600,
    background: msg.ok ? '#ecfdf5' : '#fef2f2', color: msg.ok ? '#059669' : '#dc2626',
    border: `1px solid ${msg.ok ? '#a7f3d0' : '#fecaca'}` }}>{msg.text}</div>
);
